import React, { useState } from 'react';
import {
  Button,
  ButtonSize,
  ButtonVariant,
  ActionDropdown,
  CmhcIcon,
  CmhcIconSize,
  ToastNotification,
  ToastNotificationVariant,
  Modal
} from '@cmhc-schl/hearth-ui';
import { Box, IconButton, ClickAwayListener } from '@mui/material';
import i18n from 'i18next';
import { useAuthenticationContext } from '../../../../../../../Foundation/Authentication';
import StatusPill from '../../../StatusPill';
import { PoolPrecisionTypeCode } from '../../api/types';
import { executeWorkflow, HTTP_STATUS_CONFLICT } from './hooks/portfolioCardService';
import styles from './styles.module.scss';
import {
  PortfolioCardProps,
  PortfolioNotificationConfig,
  PortfolioWorkflowActionType
} from './types';
import formatDigits from './utils/FormatDigits';
import HbtServiceErrorCodes from './utils/HbtServiceErrors';
import { ModalType } from './utils/ModalSwitcher';
import { EpipProcessingStatusCode } from '../../../HbtPortfolioManagementPage/types';
import ResetWorkflowStateIcon from 'Feature/V2/Shared_Portfolio/components/ResetIcon';
import RichTextBody from 'Feature/V2/Shared_Portfolio/components/RichTextBody/RichTextBody';
import { NotificationBannerType } from 'Feature/V2/HbtPIRequest/components/HbtPIRequestPage/types';
import { usePortfolioReleaseWorkflowState } from './hooks/usePortfolioReleaseWorkflowState';

interface PortfolioItemProps {
  portfolio: PortfolioCardProps;
  isOpen: boolean;
  anchorEl: HTMLElement | undefined;
  onToggle: (el: HTMLElement) => void;
  onClose: () => void;
  getLabel: (status: string) => string;
  t: (key: string) => string;
  isDisplayForOnlyTwoPortfolios?: boolean;
  onOpenModal: (type: ModalType, onConfirm?: () => void | Promise<void>) => Promise<boolean>;
  reloadData?: () => Promise<void>;
  onNotificationBanner?: (config: PortfolioNotificationConfig | null) => void;
}

const PortfolioItem: React.FC<PortfolioItemProps> = ({
  portfolio,
  isOpen,
  anchorEl,
  onToggle,
  onClose,
  getLabel,
  t,
  isDisplayForOnlyTwoPortfolios,
  onOpenModal,
  onNotificationBanner
}) => {
  const authenticationContext = useAuthenticationContext();
  const [showToast, setShowToast] = useState(false);
  const [toastNotificationText, setToastNotificationText] = useState({ header: '', subHeader: '' });
  const [isSelected, setIsSelected] = useState(false);
  const [isResetWorkflowModalOpen, setIsResetWorkflowModalOpen] = useState(false);
  const { mutateAsync: resetWorkflowState } = usePortfolioReleaseWorkflowState();
  const isCancellation = portfolio.poolPrecisionTypeCode === PoolPrecisionTypeCode.Cancellation;

  const showResetWorkflowErrorBanner = () => {
    onNotificationBanner?.({
      header: 'PI-Portfolio-Reset-Workflow-State-Error-Header',
      subHeader: 'PI-Portfolio-Reset-Workflow-State-Error-Body',
      variant: NotificationBannerType.ERROR
    });
  };

  const pageConfig = {
    cancelPortfolio: {
      header: 'Portfolio-Is-Successfully-Cancelled',
      subheader: 'Portfolio-Toast-Inprogress-Subheader'
    },
    rerunValidation: {
      header: 'Portfolio-Re-Run-Validation-Toast-Notification-Header',
      subheader: 'Portfolio-Toast-Subheader'
    },
    sendToCreditBureau: {
      header: 'Portfolio-Send-To-Credit-Bureau-Toast-Header',
      subheader: 'Portfolio-Toast-Subheader'
    },
    deletePortfolio: {
      header: 'Portfolio-Delete-Toast-Header',
      subheader: 'Portfolio-Toast-Inprogress-Subheader'
    }
  };

  const showWorkflowInProgressToast = () => {
    setToastNotificationText({
      header: 'Portfolio-Workflow-Inprogress-Notification-Header',
      subHeader: 'Portfolio-Workflow-Inprogress-Notification-Body'
    });
    setShowToast(true);
  };

  const isWorkflowConflictError = (error: unknown): boolean => {
    return (error as any)?.response?.status === HTTP_STATUS_CONFLICT;
  };
  const handleDeletePortfolio = async () => {
    onClose();
    const status = portfolio.epipProcessingStatusCode;
    const deletePortfolio = async () => {
      try {
        const result = await executeWorkflow(
          authenticationContext,
          PortfolioWorkflowActionType.Delete,
          portfolio.portfolioId ?? ''
        );
        if (!result.success && result.errorCode) {
          if (
            result.errorCode === HbtServiceErrorCodes.HBT_ERR_WORKFLOW_INVALID_PORTFOLIO_STATUS.code
          ) {
            isCancellation
              ? await onOpenModal('unableToDeletePortfolioErrorForCancellation')
              : await onOpenModal('unableToDeletePortfolioError');
          }
        } else {
          setToastNotificationText({
            header: pageConfig.deletePortfolio.header,
            subHeader: pageConfig.deletePortfolio.subheader
          });
          setShowToast(true);
        }
      } catch (error) {
        if (isWorkflowConflictError(error)) {
          showWorkflowInProgressToast();
          return;
        }
        console.error(
          `Error executing workflow action ${PortfolioWorkflowActionType.Delete}`,
          error
        );
      }
    };

    const requiresConfirmation = isCancellation
      ? status === EpipProcessingStatusCode.VALIDATION_COMPLETED
      : status === EpipProcessingStatusCode.VALIDATION_COMPLETED ||
        status === EpipProcessingStatusCode.RECEIVED_FROM_CREDIT_BUREAU;

    if (!requiresConfirmation) {
      await onOpenModal(
        isCancellation
          ? 'unableToDeletePortfolioErrorForCancellation'
          : 'unableToDeletePortfolioError'
      );
      return;
    }

    try {
      await onOpenModal('portfolioRequestConfirmationToDeletePortfolio', async () => {
        await deletePortfolio();
      });
    } catch (error) {
      console.error(`Error executing workflow action ${PortfolioWorkflowActionType.Delete}`, error);
    }
  };

  const handleRerunValidation = async () => {
    onClose();
    if (portfolio.epipProcessingStatusCode === EpipProcessingStatusCode.VALIDATION_COMPLETED) {
      try {
        await onOpenModal('portfolioRequestCanRunValidation', async () => {
          const result = await executeWorkflow(
            authenticationContext,
            PortfolioWorkflowActionType.Revalidate,
            portfolio.portfolioId ?? ''
          );
          if (!result.success && result.errorCode) {
            if (
              result.errorCode ===
              HbtServiceErrorCodes.HBT_ERR_WORKFLOW_INVALID_PORTFOLIO_STATUS.code
            ) {
              await onOpenModal('unableToReRunValidationError');
            }
          } else {
            setToastNotificationText({
              header: pageConfig.rerunValidation.header,
              subHeader: pageConfig.rerunValidation.subheader
            });
            setShowToast(true);
          }
        });
      } catch (error) {
        console.error(
          `Error executing workflow action ${PortfolioWorkflowActionType.Revalidate}`,
          error
        );
      }
    } else {
      await onOpenModal('unableToReRunValidationError');
    }
  };

  const cancelPortfolio = async () => {
    onClose();
    if (portfolio.epipProcessingStatusCode !== EpipProcessingStatusCode.VALIDATION_COMPLETED) {
      await onOpenModal('cancelPortfolioError');
      return;
    }
    try {
      await onOpenModal('canCancelPortfolio', async () => {
        try {
          const result = await executeWorkflow(
            authenticationContext,
            PortfolioWorkflowActionType.Cancel,
            portfolio.portfolioId ?? ''
          );
          if (!result.success && result.errorCode) {
            if (result.errorCode === HbtServiceErrorCodes.HBT_ERR_WORKFLOW_LOANS_IN_REVIEW.code) {
              await onOpenModal('cancelPortfolioErrorReview');
            } else if (
              result.errorCode ===
              HbtServiceErrorCodes.HBT_ERR_WORKFLOW_INVALID_PORTFOLIO_STATUS.code
            ) {
              await onOpenModal('cancelPortfolioError');
            }
          } else {
            setToastNotificationText({
              header: pageConfig.cancelPortfolio.header,
              subHeader: pageConfig.cancelPortfolio.subheader
            });
            setShowToast(true);
          }
        } catch (error) {
          if (isWorkflowConflictError(error)) {
            showWorkflowInProgressToast();
            return;
          }
          throw error;
        }
      });
    } catch (error) {
      console.error(`Error executing workflow action ${PortfolioWorkflowActionType.Cancel}`, error);
    }
  };

  const proceedToCredit = async () => {
    if (portfolio.epipProcessingStatusCode === EpipProcessingStatusCode.VALIDATION_COMPLETED) {
      try {
        await onOpenModal('portfolioRequestConfirmationToSendLoansToCreditBureau', async () => {
          const result = await executeWorkflow(
            authenticationContext,
            PortfolioWorkflowActionType.SentToCreditBureau,
            portfolio.portfolioId ?? ''
          );
          if (!result.success && result.errorCode) {
            if (result.errorCode === HbtServiceErrorCodes.HBT_ERR_WORKFLOW_LOANS_IN_REVIEW.code) {
              await onOpenModal('unableToProceedToCreditBureauLoansCheckError');
            } else if (
              result.errorCode ===
              HbtServiceErrorCodes.HBT_ERR_WORKFLOW_INVALID_PORTFOLIO_STATUS.code
            ) {
              await onOpenModal('cannotProceedToCreditBureauError');
            }
          } else {
            setToastNotificationText({
              header: pageConfig.sendToCreditBureau.header,
              subHeader: pageConfig.sendToCreditBureau.subheader
            });
            setShowToast(true);
          }
        });
      } catch (error) {
        console.error(
          `Error executing workflow action ${PortfolioWorkflowActionType.SentToCreditBureau}`,
          error
        );
      }
    } else {
      await onOpenModal('unableToProceedToCreditBureauLoansCheckError');
    }
  };

  const handleToggle = (event: React.MouseEvent<HTMLElement>) => {
    event.stopPropagation();
    onToggle(event.currentTarget);
  };

  const portfolioOptions = [
    {
      value: 'reRunValidation',
      label: t('PI-Portfolio-Management-Request-Overview-Re-Run-Validation-Button'),
      onClick: handleRerunValidation,
      icon: (
        <CmhcIcon size={CmhcIconSize.SMALL} type="icon_cached" testID="portfolio-item-rerun-icon" />
      )
    },
    {
      value: 'delete',
      label: t('PI-Portfolio-Management-Request-Overview-Delete-Portfolio-Card'),
      onClick: handleDeletePortfolio,
      icon: (
        <CmhcIcon
          size={CmhcIconSize.SMALL}
          type="icon_delete"
          testID="portfolio-item-delete-icon"
        />
      )
    },
    {
      value: 'resetWorkflowState',
      label: t('Reset-Workflow-State'),
      onClick: () => {
        onClose();
        onNotificationBanner?.(null);
        setIsResetWorkflowModalOpen(true);
      },
      icon: <ResetWorkflowStateIcon />
    }
  ];

  const isCancelPortfolio = isCancellation;

  const popperModifiers = [
    {
      name: 'offset',
      options: {
        offset: [i18n.language === 'en' ? -140 : -185, 0]
      }
    }
  ];
  return (
    <div
      role="button"
      tabIndex={0}
      aria-pressed={isSelected}
      aria-selected={isSelected}
      aria-label={`${t('Portfolio-ID')} ${portfolio.portfolioId}, ${t('Loans-Submitted')}: ${formatDigits(
        portfolio.loanCount,
        i18n.language
      )}, ${getLabel(portfolio.epipProcessingStatusCode)}`}
      onClick={(e: React.MouseEvent<HTMLElement>) => {
        const isInnerButton = (e.target as HTMLElement).closest('button, [role="menuitem"]');
        if (!isInnerButton) setIsSelected((prev) => !prev);
      }}
      onKeyDown={(e: React.KeyboardEvent<HTMLElement>) => {
        if ((e.key === 'Enter' || e.key === ' ') && e.target === e.currentTarget) {
          e.preventDefault();
          setIsSelected((prev) => !prev);
        }
      }}
      className={`${styles.portfolioItem} ${
        isDisplayForOnlyTwoPortfolios ? styles.portfolioMobile : styles.removeMaxWidth
      }`}
    >
      <div className={styles.portfolioItemContainer}>
        <div className={styles.porfolioItemHeader}>
          <span>
            {t('Portfolio-ID')} {portfolio.portfolioId}
          </span>
        </div>

        <ClickAwayListener onClickAway={onClose}>
          <Box>
            <IconButton
              aria-label={t('More options')}
              disableRipple
              size="small"
              className={styles.buttonMore}
              onClick={handleToggle}
            >
              <CmhcIcon
                size={CmhcIconSize.MEDIUM}
                type="icon_more_vertical"
                className={styles.icon}
              />
            </IconButton>

            <ActionDropdown
              className={styles.dropDownZIndex}
              popperModifiers={popperModifiers}
              open={isOpen}
              anchorEl={anchorEl}
              onToggle={() => {}}
              options={isCancelPortfolio ? portfolioOptions.slice(1) : portfolioOptions}
            />
          </Box>
        </ClickAwayListener>
      </div>

      <div className={styles.portfolioStatus}>
        <StatusPill
          status={portfolio.epipProcessingStatusCode}
          label={getLabel(portfolio.epipProcessingStatusCode)}
        />
      </div>

      <div className={styles.portfolioLoansSubmitted}>
        <span className={styles.portfolioSubmitted}>{t('Loans-Submitted')}</span>:
        <span className={styles.portfolioBold}>
          {' '}
          {formatDigits(portfolio.loanCount, i18n.language)}
        </span>
      </div>

      <div className={styles.buttonsContainer}>
        <Button
          className={styles.portfolioCardBtn}
          name="ProceedToCreditBtn"
          variant={ButtonVariant.SECONDARY}
          size={ButtonSize.SMALL}
          onClick={isCancelPortfolio ? cancelPortfolio : proceedToCredit}
          text={{
            value: isCancelPortfolio
              ? t('PI-Portfolio-Management-Request-Overview-Delete-Portfolio')
              : t('PI-Portfolio-Management-Request-Overview-Proceed-to-Credit-Bureau')
          }}
        />
      </div>

      {showToast && (
        <Box className={styles.toasterNotification}>
          <ToastNotification
            icon="icon_check_circle"
            dismissMode={'actionable'}
            variant={ToastNotificationVariant.INFO}
            open={showToast}
            header={{ value: t(toastNotificationText.header) }}
            subHeader={{ value: t(toastNotificationText.subHeader) }}
            onClose={() => {
              setShowToast(false);
            }}
          />
        </Box>
      )}

      <Modal
        id="portfolio-reset-workflow-state-modal"
        isActive={isResetWorkflowModalOpen}
        title={t('PI-Portfolio-Reset-Workflow-State-Modal-Title')}
        onDismissalCallback={() => setIsResetWorkflowModalOpen(false)}
        bodyContent={
          <Box className={styles.resetWorkflowModalBody}>
            <RichTextBody translationKey={t('PI-Portfolio-Reset-Workflow-State-Modal-Body')} />
          </Box>
        }
        modalButtons={{
          primaryButton: {
            name: t('Reset-Workflow-State'),
            text: {
              value: t('Reset-Workflow-State')
            } as FieldValue,
            ariaText: {
              value: t('Reset-Workflow-State')
            } as FieldValue,
            onClick: async () => {
              if (!portfolio.portfolioId) {
                showResetWorkflowErrorBanner();
                setIsResetWorkflowModalOpen(false);
                return;
              }
              setIsResetWorkflowModalOpen(false);
              try {
                const response = await resetWorkflowState({
                  portfolioId: portfolio.portfolioId
                });

                if (response) {
                  setToastNotificationText({
                    header: 'PI-Portfolio-Reset-Workflow-State-Toast-Header',
                    subHeader: 'PI-Portfolio-Reset-Workflow-State-Toast-Body'
                  });
                  setShowToast(true);
                  onNotificationBanner?.(null);
                  return;
                }

                throw new Error('reset_workflow_failed');
              } catch (error) {
                showResetWorkflowErrorBanner();
              }
            }
          },
          secondaryButton: {
            name: t('Cancel'),
            text: { value: t('Cancel') } as FieldValue,
            ariaText: { value: t('Cancel') } as FieldValue,
            onClick: () => setIsResetWorkflowModalOpen(false)
          }
        }}
        shouldCloseOnBackgroundClick={true}
      />
    </div>
  );
};

export default PortfolioItem;
